/**
 * Custom hooks per gestione profilo utente
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast-service";
import {
  updateProfile,
  changePassword,
  uploadAvatar,
  getUserEmailAccounts,
  getUserEmailAccount,
  startEmailOAuthFlow,
  completeEmailOAuthCallback,
  disconnectEmailAccount,
} from "@/data/api/profile";
import type {
  UpdateProfileRequest,
  ChangePasswordRequest,
  UploadAvatarRequest,
  OAuthCallbackRequest,
} from "@/types/api/profile";
import type { UserInfo } from "@/types/api/auth";

const PROFILE_QUERY_KEYS = {
  me: ["auth", "me"] as const,
  emailAccounts: ["user-email-accounts"] as const,
  emailAccount: (id: string) => [...PROFILE_QUERY_KEYS.emailAccounts, id] as const,
};

/**
 * Hook per aggiornare il profilo utente
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => updateProfile(data),
    onSuccess: (data: UserInfo) => {
      // Aggiorna i dati utente nella cache
      queryClient.setQueryData(PROFILE_QUERY_KEYS.me, data);
      toast.success("Profilo aggiornato con successo");
    },
    onError: (error: any) => {
      console.error("Error updating profile:", error);
      toast.error(error?.message || "Errore durante l'aggiornamento del profilo");
    },
  });
}

/**
 * Hook per cambiare password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => changePassword(data),
    onSuccess: () => {
      toast.success("Password cambiata con successo");
    },
    onError: (error: any) => {
      console.error("Error changing password:", error);
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        "Errore durante il cambio password";
      toast.error(message);
    },
  });
}

/**
 * Hook per upload immagine profilo
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UploadAvatarRequest) => uploadAvatar(data),
    onSuccess: (data) => {
      // Aggiorna i dati utente nella cache con la nuova avatar URL
      queryClient.setQueryData<UserInfo>(PROFILE_QUERY_KEYS.me, (old) => {
        if (!old) return old;
        return { ...old, avatar_url: data.avatar_url };
      });
      toast.success("Immagine profilo caricata con successo");
    },
    onError: (error: any) => {
      console.error("Error uploading avatar:", error);
      toast.error(error?.message || "Errore durante il caricamento dell'immagine");
    },
  });
}

/**
 * Hook per ottenere la lista degli account email dell'utente
 */
export function useUserEmailAccounts() {
  return useQuery({
    queryKey: PROFILE_QUERY_KEYS.emailAccounts,
    queryFn: getUserEmailAccounts,
    staleTime: 5 * 60 * 1000, // 5 minuti
  });
}

/**
 * Hook per ottenere un account email specifico
 */
export function useUserEmailAccount(accountId: string) {
  return useQuery({
    queryKey: PROFILE_QUERY_KEYS.emailAccount(accountId),
    queryFn: () => getUserEmailAccount(accountId),
    enabled: !!accountId,
  });
}

/**
 * Hook per iniziare OAuth flow per email account
 */
export function useStartEmailOAuth() {
  return useMutation({
    mutationFn: ({ provider, redirectUri }: { provider: "gmail" | "outlook"; redirectUri: string }) =>
      startEmailOAuthFlow(provider, redirectUri),
    onError: (error: any) => {
      console.error("Error starting OAuth flow:", error);
      toast.error(error?.message || "Errore durante l'avvio del processo di autorizzazione");
    },
  });
}

/**
 * Hook per completare OAuth callback
 */
export function useCompleteEmailOAuth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      provider,
      data,
      accountId,
    }: {
      provider: "gmail" | "outlook";
      data: OAuthCallbackRequest;
      accountId: string;
    }) => completeEmailOAuthCallback(provider, data, accountId),
    onSuccess: () => {
      // Invalida la lista degli account email
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEYS.emailAccounts });
      toast.success("Account email collegato con successo");
    },
    onError: (error: any) => {
      console.error("Error completing OAuth:", error);
      toast.error(error?.message || "Errore durante la connessione dell'account email");
    },
  });
}

/**
 * Hook per disconnettere un account email
 */
export function useDisconnectEmailAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => disconnectEmailAccount(accountId),
    onSuccess: () => {
      // Invalida la lista degli account email
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEYS.emailAccounts });
      toast.success("Account email disconnesso con successo");
    },
    onError: (error: any) => {
      console.error("Error disconnecting email account:", error);
      toast.error(error?.message || "Errore durante la disconnessione dell'account");
    },
  });
}

